import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";

import { useCalendar } from "../../../api/hooks/useCalendar";
import { useDrawdown } from "../../../api/hooks/useDrawdown";
import { useEquityCurve } from "../../../api/hooks/useEquityCurve";
import { usePnLByDay } from "../../../api/hooks/usePnLByDay";
import { useSummary } from "../../../api/hooks/useSummary";
import { useTrades } from "../../../api/hooks/useTrades";
import { useWinLossByDay } from "../../../api/hooks/useWinLossByDay";
import type { components } from "../../../api/schema";
import { DailyPnLChart } from "../../charts/DailyPnLChart";
import { DrawdownChart } from "../../charts/DrawdownChart";
import { EquityCurveChart } from "../../charts/EquityCurveChart";
import { WinsLossesChart } from "../../charts/WinsLossesChart";
import { NEGATIVE_COLOR, POSITIVE_COLOR } from "../../charts/chartOptions";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { TableSkeleton } from "../../ui/skeleton";
import { useDashboardFilters } from "../../../lib/dashboardFilters";
import { formatDate, formatTimeShort } from "../../../lib/date";
import { monthBounds } from "../../../lib/journal";
import { formatMoney, formatSignedMoney, sumDecimals } from "../../../lib/money";
import { formatPercent } from "../../../lib/tradeFormat";

type TradeSummary = components["schemas"]["TradeSummaryResponse"];

const PERCENT_FRACTION_DIGITS = 1;
// De-crowd cap (L0-U16): the panel is a top-N preview; the "View all" footer
// drills through to the full /trades list for everything beyond this.
const TRADES_PANEL_LIMIT = 10;

const WEEKDAY_HEADERS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"] as const;
const WEEK_ROWS = 6;
const DAYS_PER_WEEK = 7;

function formatWinRate(value: string): string {
  const ratio = Number(value);
  if (!Number.isFinite(ratio)) return "—";
  return `${(ratio * 100).toFixed(PERCENT_FRACTION_DIGITS)}%`;
}

function formatProfitFactor(value: string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const ratio = Number(value);
  if (!Number.isFinite(ratio)) return "—";
  return ratio.toFixed(2);
}

function sumDailyPnL(points: ReadonlyArray<{ pnl_amount: string }>): string {
  return sumDecimals(points.map((p) => p.pnl_amount));
}

// Compact "kbig" KPI card (WickScope bento): label top-left, a large mono
// value that fills the card's vertical space, an optional sub line, and an
// optional decorative art (sparkline / ring) in the top-right corner.
function KpiCard({
  label,
  value,
  sub,
  tone = "neutral",
  art,
  state = "ready",
  isPlaceholder = false,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  tone?: "pos" | "neg" | "neutral";
  art?: ReactNode;
  state?: "loading" | "error" | "ready";
  isPlaceholder?: boolean;
}) {
  const toneClass =
    tone === "pos"
      ? "text-success"
      : tone === "neg"
        ? "text-danger"
        : "text-on-surface";
  let display: ReactNode;
  if (state === "loading") {
    display = <span className="text-sm text-on-surface-muted">Loading…</span>;
  } else if (state === "error") {
    display = (
      <span role="alert" className="text-sm text-danger">
        Unavailable
      </span>
    );
  } else {
    display = (
      <span
        className={`font-data text-[2rem] font-semibold leading-none tracking-tight ${
          isPlaceholder ? "text-on-surface" : toneClass
        }`}
      >
        {value}
      </span>
    );
  }
  return (
    <Card className="relative flex h-full flex-col overflow-hidden p-4">
      <span className="text-xs font-medium text-on-surface-variant">
        {label}
      </span>
      <div className="mt-auto flex flex-col gap-1.5 pt-3">
        {display}
        {sub !== undefined ? (
          <span className="text-[11px] text-on-surface-muted">{sub}</span>
        ) : null}
      </div>
      {art !== undefined ? (
        <div className="pointer-events-none absolute right-3 top-3">{art}</div>
      ) : null}
    </Card>
  );
}

// Tiny cumulative-P&L sparkline for the Net P&L card's corner art.
function Sparkline({ values, tone }: { values: readonly number[]; tone: "pos" | "neg" }) {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 58;
  const h = 24;
  const stroke = tone === "neg" ? "var(--color-danger)" : "var(--color-success)";
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" L ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <path
        d={`M ${pts}`}
        fill="none"
        stroke={stroke}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Win-rate donut ring for the Win rate card's corner art.
function Ring({ ratio }: { ratio: number }) {
  const r = 15;
  const circumference = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(1, ratio));
  return (
    <svg width={42} height={42} viewBox="0 0 40 40" aria-hidden="true">
      <circle cx={20} cy={20} r={r} stroke="var(--color-outline-variant)" strokeWidth={4} fill="none" />
      <circle
        cx={20}
        cy={20}
        r={r}
        stroke="var(--color-success)"
        strokeWidth={4}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - clamped)}
        transform="rotate(-90 20 20)"
      />
    </svg>
  );
}

function cumulativeValues(points: ReadonlyArray<{ pnl_amount: string }>): number[] {
  let running = 0;
  return points.map((p) => {
    running += Number(p.pnl_amount);
    return running;
  });
}

export function KpiTotalPnlWidget() {
  const { filters } = useDashboardFilters();
  const pnl = usePnLByDay(filters);
  if (pnl.isLoading) return <KpiCard label="Net P&L" value="" state="loading" />;
  if (pnl.isError || pnl.data === undefined) {
    return <KpiCard label="Net P&L" value="" state="error" />;
  }
  const total = sumDailyPnL(pnl.data.points);
  const numeric = Number(total);
  const tone = numeric > 0 ? "pos" : numeric < 0 ? "neg" : "neutral";
  const cumulative = cumulativeValues(pnl.data.points);
  return (
    <KpiCard
      label="Net P&L"
      value={formatSignedMoney(total, { currency: pnl.data.currency })}
      tone={tone}
      sub="Closed P&L"
      art={
        cumulative.length >= 2 ? (
          <Sparkline values={cumulative} tone={numeric < 0 ? "neg" : "pos"} />
        ) : undefined
      }
    />
  );
}

export function KpiWinRateWidget() {
  const { filters } = useDashboardFilters();
  const summary = useSummary(filters);
  if (summary.isLoading) {
    return <KpiCard label="Win rate" value="" state="loading" />;
  }
  if (summary.isError || summary.data === undefined) {
    return <KpiCard label="Win rate" value="" state="error" />;
  }
  const ratio = Number(summary.data.win_rate);
  return (
    <KpiCard
      label="Win rate"
      value={formatWinRate(summary.data.win_rate)}
      sub={`${summary.data.win_count} W · ${summary.data.loss_count} L`}
      art={Number.isFinite(ratio) ? <Ring ratio={ratio} /> : undefined}
    />
  );
}

export function KpiProfitFactorWidget() {
  const { filters } = useDashboardFilters();
  const summary = useSummary(filters);
  if (summary.isLoading) return <KpiCard label="Profit factor" value="" state="loading" />;
  if (summary.isError || summary.data === undefined) {
    return <KpiCard label="Profit factor" value="" state="error" />;
  }
  const value = formatProfitFactor(summary.data.profit_factor);
  return (
    <KpiCard
      label="Profit factor"
      value={value}
      sub="Gross profit ÷ loss"
      isPlaceholder={value === "—"}
    />
  );
}

export function KpiAvgPnlWidget() {
  const { filters } = useDashboardFilters();
  const summary = useSummary(filters);
  if (summary.isLoading) return <KpiCard label="Avg / trade" value="" state="loading" />;
  if (summary.isError || summary.data === undefined) {
    return <KpiCard label="Avg / trade" value="" state="error" />;
  }
  const numeric = Number(summary.data.expectancy_amount);
  const tone = numeric > 0 ? "pos" : numeric < 0 ? "neg" : "neutral";
  return (
    <KpiCard
      label="Avg / trade"
      value={formatSignedMoney(summary.data.expectancy_amount, {
        currency: summary.data.expectancy_currency,
      })}
      tone={tone}
      sub={`per trade · ${summary.data.trade_count}`}
    />
  );
}

function ChartShell({
  title,
  legend,
  children,
}: {
  title: React.ReactNode;
  legend: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className="flex h-full flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-normal text-on-surface-variant">
            {title}
          </CardTitle>
          {legend}
        </div>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 overflow-hidden">{children}</CardContent>
    </Card>
  );
}

export function WinsLossesChartWidget() {
  const { filters } = useDashboardFilters();
  const winLoss = useWinLossByDay(filters);
  const points = winLoss.data?.points;
  const totalWins = points?.reduce((acc, p) => acc + p.wins, 0) ?? 0;
  const totalLosses = points?.reduce((acc, p) => acc + p.losses, 0) ?? 0;
  const title = (
    <span className="flex items-baseline gap-2">
      <span>Daily Wins/Losses</span>
      {points !== undefined ? (
        <span
          data-testid="dashboard-winloss-total"
          className="text-xs tabular-nums text-on-surface-variant"
        >
          {totalWins}W / {totalLosses}L
        </span>
      ) : null}
    </span>
  );
  const legend = (
    <div className="flex gap-3 text-xs text-on-surface-variant">
      <span className="flex items-center gap-1">
        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: POSITIVE_COLOR }} />
        Wins
      </span>
      <span className="flex items-center gap-1">
        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: NEGATIVE_COLOR }} />
        Losses
      </span>
    </div>
  );
  return (
    <ChartShell title={title} legend={legend}>
      {winLoss.isLoading ? (
        <p className="text-sm text-on-surface-variant">Loading…</p>
      ) : winLoss.isError || points === undefined ? (
        <p role="alert" className="text-sm text-danger">Unavailable</p>
      ) : (
        <WinsLossesChart points={points} fill />
      )}
    </ChartShell>
  );
}

// Hero chart card (WickScope bento): a large, un-squished mono headline value
// over a full-height chart. Used by the Cumulative P&L and Daily Net P&L tiles
// so the figure uses the available space the design's small pills wasted.
function HeroChartCard({
  title,
  value,
  tone = "neutral",
  state,
  children,
}: {
  title: string;
  value: string;
  tone?: "pos" | "neg" | "neutral";
  state: "loading" | "error" | "ready";
  children: ReactNode;
}) {
  const toneClass =
    tone === "pos"
      ? "text-success"
      : tone === "neg"
        ? "text-danger"
        : "text-on-surface";
  return (
    <Card className="flex h-full flex-col p-4">
      <span className="text-sm font-semibold text-on-surface">{title}</span>
      <span
        className={`mt-1 font-data text-[2rem] font-semibold leading-none tracking-tight ${toneClass}`}
      >
        {state === "loading" ? "—" : value}
      </span>
      <div className="mt-3 min-h-0 flex-1 overflow-hidden">
        {state === "loading" ? (
          <p className="text-sm text-on-surface-variant">Loading…</p>
        ) : state === "error" ? (
          <p role="alert" className="text-sm text-danger">
            Unavailable
          </p>
        ) : (
          children
        )}
      </div>
    </Card>
  );
}

function toneForNumber(value: number): "pos" | "neg" | "neutral" {
  if (value > 0) return "pos";
  if (value < 0) return "neg";
  return "neutral";
}

// Title-only chart card: header label over a full-height chart, no headline
// value. Used where a sibling tile already owns the summary figure.
function SimpleChartCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Card className="flex h-full flex-col p-4">
      <span className="text-sm font-semibold text-on-surface">{title}</span>
      <div className="mt-3 min-h-0 flex-1 overflow-hidden">{children}</div>
    </Card>
  );
}

export function EquityCurveChartWidget() {
  const { filters } = useDashboardFilters();
  const equity = useEquityCurve(filters);
  const state = equity.isLoading
    ? "loading"
    : equity.isError || equity.data === undefined
      ? "error"
      : "ready";
  const points = equity.data?.points ?? [];
  const last = points[points.length - 1];
  const net = last?.cumulative_pnl_amount ?? "0";
  return (
    <HeroChartCard
      title="Cumulative P&L"
      value={formatSignedMoney(net, { currency: equity.data?.currency ?? "USD" })}
      tone={toneForNumber(Number(net))}
      state={state}
    >
      {equity.data !== undefined ? (
        <EquityCurveChart
          points={equity.data.points}
          currency={equity.data.currency}
          tone="success"
          axis
          baselineZero
          fill
        />
      ) : null}
    </HeroChartCard>
  );
}

// Daily Net P&L: the total figure is already headlined by the Net P&L KPI, so
// this tile is just a title + the per-day bar chart.
export function DailyPnLChartWidget() {
  const { filters } = useDashboardFilters();
  const pnl = usePnLByDay(filters);
  return (
    <SimpleChartCard title="Daily Net P&L">
      {pnl.isLoading ? (
        <p className="text-sm text-on-surface-variant">Loading…</p>
      ) : pnl.isError || pnl.data === undefined ? (
        <p role="alert" className="text-sm text-danger">
          Unavailable
        </p>
      ) : (
        <DailyPnLChart points={pnl.data.points} currency={pnl.data.currency} axis fill />
      )}
    </SimpleChartCard>
  );
}

// "Last 14 sessions" streak (WickScope bento): one vertical bar per recent
// session, green for a winning day and red for a losing one, sized by the
// magnitude of that day's net P&L. A header pill tallies green vs red.
export function StreakWidget() {
  const { filters } = useDashboardFilters();
  const pnl = usePnLByDay(filters);
  const points = pnl.data?.points ?? [];
  const recent = points.slice(-14);
  const values = recent.map((p) => Number(p.pnl_amount));
  const maxAbs = Math.max(1, ...values.map((v) => Math.abs(v)));
  const greenCount = values.filter((v) => v > 0).length;
  const redCount = values.filter((v) => v < 0).length;
  return (
    <Card className="flex h-full flex-col p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-on-surface">Last 14 sessions</span>
        {pnl.data !== undefined ? (
          <span className="rounded-pill bg-surface-container-low px-2 py-0.5 text-[11px] font-medium text-on-surface-variant">
            {greenCount} green · {redCount} red
          </span>
        ) : null}
      </div>
      <div className="mt-3 min-h-0 flex-1">
        {pnl.isLoading ? (
          <p className="text-sm text-on-surface-variant">Loading…</p>
        ) : pnl.isError || pnl.data === undefined ? (
          <p role="alert" className="text-sm text-danger">
            Unavailable
          </p>
        ) : values.length === 0 ? (
          <p className="text-sm text-on-surface-muted">No sessions in range.</p>
        ) : (
          <div className="flex h-full items-end gap-1.5" aria-hidden="true">
            {values.map((v, i) => {
              const pct = 25 + (Math.abs(v) / maxAbs) * 75;
              return (
                <span
                  key={i}
                  className="flex-1 rounded-[2px]"
                  style={{
                    height: `${pct}%`,
                    backgroundColor:
                      v >= 0 ? "var(--color-success)" : "var(--color-danger)",
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

export function DrawdownChartWidget() {
  const { filters } = useDashboardFilters();
  const drawdown = useDrawdown(filters);
  const legend = (
    <span className="text-xs text-on-surface-variant">
      {drawdown.data === undefined || drawdown.data.max_drawdown_date === null
        ? "Max: —"
        : `Max: ${formatMoney(drawdown.data.max_drawdown_amount, { currency: drawdown.data.currency })}`}
    </span>
  );
  return (
    <ChartShell title="Drawdown" legend={legend}>
      {drawdown.isLoading ? (
        <p className="text-sm text-on-surface-variant">Loading…</p>
      ) : drawdown.isError || drawdown.data === undefined ? (
        <p role="alert" className="text-sm text-danger">Unavailable</p>
      ) : (
        <DrawdownChart points={drawdown.data.points} currency={drawdown.data.currency} fill />
      )}
    </ChartShell>
  );
}

// Row dot color keyed to the trade's outcome: green for a profit, red for a
// loss, blue for a scratch (zero) or still-open trade (no realized P&L yet).
const SCRATCH_DOT = "#3b82f6";
function pnlDotColor(value: string | null | undefined): string {
  if (value === null || value === undefined) return SCRATCH_DOT;
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return SCRATCH_DOT;
  return n > 0 ? "var(--color-success)" : "var(--color-danger)";
}

function pnlToneClass(value: string | null | undefined): string {
  if (value === null || value === undefined) return "text-on-surface-variant";
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "text-on-surface-variant";
  return n > 0 ? "text-success" : "text-danger";
}

// Long / Short pill from the trade's opening leg direction. Unknown (legless)
// renders an em-dash so the column stays aligned.
function SideBadge({ direction }: { direction: string | null | undefined }) {
  if (direction !== "long" && direction !== "short") {
    return <span className="text-on-surface-muted">—</span>;
  }
  const isLong = direction === "long";
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
        isLong ? "bg-success-soft text-success" : "bg-primary/10 text-primary"
      }`}
    >
      {isLong ? "Long" : "Short"}
    </span>
  );
}

function CompactTradeRow({
  trade,
  onOpen,
}: {
  trade: TradeSummary;
  onOpen: (tradeId: string) => void;
}) {
  const symbol = trade.primary_symbol ?? "—";
  const pnl = trade.realized_pnl_amount;
  return (
    <tr
      role="link"
      tabIndex={0}
      aria-label={`Open trade ${symbol}`}
      onClick={() => onOpen(trade.trade_id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(trade.trade_id);
        }
      }}
      className="cursor-pointer border-b border-outline-variant/70 last:border-0 hover:bg-surface-container-low focus:bg-surface-container-low focus:outline-none"
    >
      <td className="whitespace-nowrap py-2 pr-3">
        <span className="text-primary">{formatDate(trade.opened_at)}</span>
        <span className="ml-2 font-data text-on-surface-muted">
          {formatTimeShort(trade.opened_at)}
        </span>
      </td>
      <td className="py-2 pr-3">
        <span className="flex items-center gap-2 font-medium text-on-surface">
          <span
            aria-hidden="true"
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: pnlDotColor(pnl) }}
          />
          {symbol}
        </span>
      </td>
      <td className="py-2 pr-3">
        <SideBadge direction={trade.primary_direction} />
      </td>
      <td
        className={`whitespace-nowrap py-2 pr-3 text-right font-data tabular-nums ${pnlToneClass(pnl)}`}
      >
        {pnl === null || pnl === undefined
          ? "—"
          : formatMoney(pnl, { currency: trade.currency })}
      </td>
      <td
        className={`whitespace-nowrap py-2 text-right font-data tabular-nums ${pnlToneClass(trade.realized_return_pct)}`}
      >
        {formatPercent(trade.realized_return_pct)}
      </td>
    </tr>
  );
}

export function TradesPanelWidget() {
  const { filters } = useDashboardFilters();
  const navigate = useNavigate();
  // Apply the dashboard's active date window to the panel so it honors the
  // same filter every other tile does. (account/tag aren't list-query axes —
  // the /api/trades endpoint filters by opened_at, matching the Trades page.)
  const openedAfter =
    filters.since !== undefined ? `${filters.since}T00:00:00.000Z` : undefined;
  const openedBefore =
    filters.until !== undefined ? `${filters.until}T23:59:59.999Z` : undefined;
  const { data, isLoading, isError } = useTrades({
    limit: TRADES_PANEL_LIMIT,
    sort_key: "opened_at",
    sort_order: "desc",
    ...(openedAfter !== undefined ? { opened_after: openedAfter } : {}),
    ...(openedBefore !== undefined ? { opened_before: openedBefore } : {}),
  });

  // Drill-through carries the active date window so the full list opens scoped
  // to the same range the dashboard is showing.
  const viewAllSearch = {
    ...(filters.since !== undefined ? { since: filters.since } : {}),
    ...(filters.until !== undefined ? { until: filters.until } : {}),
  };

  const rows = data?.items ?? [];
  const openTrade = (tradeId: string) => {
    void navigate({
      to: "/trades/$tradeId",
      params: { tradeId },
      search: { from: "dashboard" },
    });
  };

  return (
    <Card className="flex h-full flex-col p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-on-surface">Recent trades</span>
        <Link
          to="/trades"
          search={viewAllSearch}
          className="text-xs font-semibold text-primary hover:text-primary-hover"
        >
          All →
        </Link>
      </div>
      <div className="mt-2 min-h-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="py-2" role="status" aria-live="polite">
            <span className="sr-only">Loading trades</span>
            <TableSkeleton rows={5} />
          </div>
        ) : isError ? (
          <p className="py-4 text-center text-sm text-danger" role="alert">
            Failed to load trades.
          </p>
        ) : rows.length === 0 ? (
          <p className="py-4 text-center text-sm text-on-surface-muted">
            No trades to show.
          </p>
        ) : (
          <table className="w-full table-auto text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.09em] text-on-surface-muted">
                <th className="py-1 pr-3 text-left font-semibold">Date / Time</th>
                <th className="py-1 pr-3 text-left font-semibold">Symbol</th>
                <th className="py-1 pr-3 text-left font-semibold">Side</th>
                <th className="py-1 pr-3 text-right font-semibold">P&amp;L</th>
                <th className="py-1 text-right font-semibold">P&amp;L %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((trade) => (
                <CompactTradeRow key={trade.trade_id} trade={trade} onOpen={openTrade} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}

interface CalendarCell {
  isoDate: string | null;
  day: number | null;
}

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

interface MonthCursor {
  year: number;
  month: number;
}

function monthCursorFromNow(now: Date): MonthCursor {
  return { year: now.getUTCFullYear(), month: now.getUTCMonth() };
}

// Today's date as a UTC ISO day string ("YYYY-MM-DD"), matching the calendar
// grid's UTC day cells so the "today" outline lands on the right cell.
function isoForToday(): string {
  const now = new Date();
  return `${now.getUTCFullYear().toString().padStart(4, "0")}-${pad2(
    now.getUTCMonth() + 1,
  )}-${pad2(now.getUTCDate())}`;
}

function shiftMonth(cursor: MonthCursor, delta: number): MonthCursor {
  const total = cursor.year * 12 + cursor.month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

function buildMonthGrid(cursor: MonthCursor): {
  cells: CalendarCell[][];
  monthLabel: string;
} {
  const year = cursor.year;
  const month = cursor.month;
  const monthName = new Intl.DateTimeFormat("en-US", {
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month, 1)));
  const firstDayOfWeek = new Date(Date.UTC(year, month, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

  const cells: CalendarCell[][] = [];
  let cursorDay = 1 - firstDayOfWeek;
  for (let w = 0; w < WEEK_ROWS; w += 1) {
    const row: CalendarCell[] = [];
    for (let d = 0; d < DAYS_PER_WEEK; d += 1) {
      if (cursorDay >= 1 && cursorDay <= daysInMonth) {
        const iso = `${year.toString().padStart(4, "0")}-${pad2(month + 1)}-${pad2(cursorDay)}`;
        row.push({ isoDate: iso, day: cursorDay });
      } else {
        row.push({ isoDate: null, day: null });
      }
      cursorDay += 1;
    }
    cells.push(row);
  }
  return { cells, monthLabel: `${monthName} ${year}` };
}

interface DayBucket {
  trade_count: number;
  // The dashboard calendar surfaces gross P&L (before commissions). Falls back
  // to net for series predating the gross field.
  gross_pnl_amount: string;
}

function indexCalendarByDate(
  points: ReadonlyArray<{
    trade_date: string;
    trade_count: number;
    net_pnl_amount: string;
    gross_pnl_amount?: string;
  }>,
): Map<string, DayBucket> {
  const map = new Map<string, DayBucket>();
  for (const p of points) {
    map.set(p.trade_date, {
      trade_count: p.trade_count,
      gross_pnl_amount: p.gross_pnl_amount ?? p.net_pnl_amount,
    });
  }
  return map;
}

// One day in the mini calendar. Rounded, padded "card" cells separated by the
// table's border-spacing (WickScope design): day number sits top-right, the
// day's net P&L at the bottom, with green/red tints for gain/loss days and an
// accent outline on today.
function CalendarMiniCell({
  cell,
  bucket,
  currency,
  isToday,
  onSelect,
}: {
  cell: CalendarCell;
  bucket: DayBucket | undefined;
  currency: string;
  isToday: boolean;
  onSelect: (isoDate: string) => void;
}) {
  if (cell.isoDate === null) {
    // Off-month: render an empty, untinted cell so only the spacing shows.
    return <td className="p-0" aria-hidden="true" />;
  }
  const isoDate = cell.isoDate;
  let tintClass = "border-outline-variant bg-surface-container-high";
  let amountClass = "text-on-surface-variant";
  if (bucket !== undefined) {
    const amount = Number(bucket.gross_pnl_amount);
    if (amount > 0) {
      tintClass = "border-success bg-success-soft";
      amountClass = "text-success";
    } else if (amount < 0) {
      tintClass = "border-danger bg-danger-soft";
      amountClass = "text-danger";
    }
  }
  const cellClass = `flex h-full min-h-[2.5rem] w-full flex-col rounded-lg border px-1.5 py-1 text-left ${tintClass} ${
    isToday ? "outline outline-2 -outline-offset-2 outline-primary" : ""
  }`;
  const inner = (
    <>
      <span className="self-end text-[11px] font-semibold text-on-surface-variant">
        {cell.day}
      </span>
      {bucket !== undefined ? (
        <span className={`mt-auto text-xs font-semibold ${amountClass}`}>
          {formatMoney(bucket.gross_pnl_amount, { currency })}
        </span>
      ) : null}
    </>
  );
  // Days with trades drill into that day's Trades list (mirrors the Calendar
  // page); empty days are non-interactive.
  return (
    <td className="p-0 align-top">
      {bucket !== undefined ? (
        <button
          type="button"
          data-testid={`dashboard-calendar-cell-${isoDate}`}
          aria-label={`View trades for ${isoDate}`}
          onClick={() => onSelect(isoDate)}
          className={`${cellClass} cursor-pointer transition-colors hover:brightness-95`}
        >
          {inner}
        </button>
      ) : (
        <div data-testid={`dashboard-calendar-cell-${isoDate}`} className={cellClass}>
          {inner}
        </div>
      )}
    </td>
  );
}

// The visible month's UTC bounds. The dashboard calendar intentionally ignores
// the dashboard's date-range presets (This month / 7d / 30d / 90d / YTD / All)
// and account/tag filters -- it always shows the month the user is browsing.
// Delegates to the shared monthBounds helper (lib/journal) so the dashboard
// calendar widget uses the same closed-interval upper bound as the full
// calendar and Notes pages -- see monthBounds for the ~1s-gap rationale.
function monthFilters(cursor: MonthCursor): { since: string; until: string } {
  return monthBounds(cursor);
}

// Gross P&L + trade count for the visible month. Points outside the cursor
// month are ignored so the figure stays exact even if the API returns a
// wider window. Gross falls back to net for series predating the gross field.
function widgetMonthTotal(
  points: ReadonlyArray<{
    trade_date: string;
    trade_count: number;
    net_pnl_amount: string;
    gross_pnl_amount?: string;
  }>,
  cursor: MonthCursor,
): { tradeCount: number; grossPnl: string } {
  let count = 0;
  const grosses: string[] = [];
  for (const p of points) {
    const [y = 0, m = 0] = p.trade_date.split("-").map(Number);
    if (y !== cursor.year || m - 1 !== cursor.month) continue;
    count += p.trade_count;
    grosses.push(p.gross_pnl_amount ?? p.net_pnl_amount);
  }
  return { tradeCount: count, grossPnl: sumDecimals(grosses) };
}

export function CalendarPanelWidget() {
  const navigate = useNavigate();
  // Returning from a day drill-in ("Back to dashboard") carries cal_y/cal_m so
  // the widget reopens on the month the user was browsing.
  const restored = useSearch({ strict: false }) as {
    cal_y?: number;
    cal_m?: number;
  };
  const [cursor, setCursor] = useState<MonthCursor>(() =>
    typeof restored.cal_y === "number" && typeof restored.cal_m === "number"
      ? { year: restored.cal_y, month: restored.cal_m }
      : monthCursorFromNow(new Date()),
  );

  // The calendar is driven solely by its own month arrows -- it deliberately
  // does not follow the dashboard's date-range presets or account/tag filters.
  const filters = useMemo(() => monthFilters(cursor), [cursor]);
  const { data, isLoading, isError } = useCalendar(filters);
  const openDay = (isoDate: string) => {
    void navigate({
      to: "/trades",
      search: {
        since: isoDate,
        until: isoDate,
        from: "dashboard",
        cal_y: cursor.year,
        cal_m: cursor.month,
      },
    });
  };
  const grid = useMemo(() => buildMonthGrid(cursor), [cursor]);
  const points = data?.points ?? [];
  const buckets = useMemo(() => indexCalendarByDate(points), [points]);
  const total = useMemo(
    () => widgetMonthTotal(points, cursor),
    [points, cursor],
  );
  const currency = data?.currency ?? "USD";
  const totalAmount = Number(total.grossPnl);
  const totalClass =
    totalAmount > 0
      ? "text-success"
      : totalAmount < 0
        ? "text-danger"
        : "text-on-surface";
  const todayIso = isoForToday();

  return (
    <Card className="flex h-full flex-col p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setCursor((c) => shiftMonth(c, -1))}
            aria-label="Previous month"
            className="rounded-md border border-outline px-1.5 py-0.5 text-xs text-on-surface hover:bg-surface-container-high"
          >
            ‹
          </button>
          <span
            className="min-w-24 text-center text-sm font-semibold text-on-surface"
            data-testid="dashboard-calendar-month"
          >
            {grid.monthLabel}
          </span>
          <button
            type="button"
            onClick={() => setCursor((c) => shiftMonth(c, 1))}
            aria-label="Next month"
            className="rounded-md border border-outline px-1.5 py-0.5 text-xs text-on-surface hover:bg-surface-container-high"
          >
            ›
          </button>
        </div>
        <p
          className="text-xs text-on-surface-variant"
          data-testid="dashboard-calendar-month-total"
        >
          Gross{" "}
          <span className={`font-semibold ${totalClass}`}>
            {formatMoney(total.grossPnl, { currency })}
          </span>{" "}
          · {total.tradeCount} {total.tradeCount === 1 ? "trade" : "trades"}
        </p>
      </div>
      <div className="mt-3 min-h-0 flex-1 overflow-hidden">
        {isLoading ? (
          <p className="py-4 text-center text-xs text-on-surface-variant" role="status" aria-live="polite">
            Loading calendar…
          </p>
        ) : isError ? (
          <p className="py-4 text-center text-xs text-danger" role="alert">
            Failed to load calendar.
          </p>
        ) : (
          <table
            className="h-full w-full table-fixed border-separate border-spacing-1.5 text-xs"
            aria-label="Monthly P&L calendar"
          >
            <thead>
              <tr>
                {WEEKDAY_HEADERS.map((d) => (
                  <th key={d} scope="col" className="pb-1 font-semibold text-on-surface-muted">
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.cells.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <CalendarMiniCell
                      key={ci}
                      cell={cell}
                      bucket={cell.isoDate ? buckets.get(cell.isoDate) : undefined}
                      currency={currency}
                      isToday={cell.isoDate !== null && cell.isoDate === todayIso}
                      onSelect={openDay}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}
